import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  DateRange as DateRangeIcon,
  DeviceHub as DeviceHubIcon,
  Assignment as LicenseIcon,
  Apps as AppsIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { UserProfile, UserStats, UpdateProfileRequest, userApi } from '../api/user';
import { fetchUserLicenses } from '../api/license';

interface UserLicense {
  id: string;
  status: 'active' | 'inactive' | 'expired';
  activationCount: number;
  maxActivations: number;
  expiryDate: string;
  devices: Array<{
    id: string;
    deviceId: string;
    status: 'active' | 'inactive';
    activatedAt: string;
    lastCheckedAt: string;
  }>;
}

const updateProfileSchema = z.object({
  given_name: z.string().min(1, '名が必須です'),
  family_name: z.string().min(1, '姓が必須です'),
});

interface ProfileEditDialogProps {
  profile: UserProfile | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: UpdateProfileRequest) => void;
}

const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({ profile, open, onClose, onSave }) => {
  const { control, handleSubmit, formState: { errors } } = useForm<UpdateProfileRequest>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      given_name: profile?.given_name || '',
      family_name: profile?.family_name || '',
    }
  });

  const onSubmit = (data: UpdateProfileRequest) => {
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>プロフィール編集</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="family_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="姓"
                    error={!!errors.family_name}
                    helperText={errors.family_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="given_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="名"
                    error={!!errors.given_name}
                    helperText={errors.given_name?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button type="submit" variant="contained">
            保存
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default function MyPage() {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: userApi.getProfile,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats'],
    queryFn: userApi.getStats,
  });

  const { data: licenses, isLoading: licensesLoading } = useQuery<UserLicense[]>({
    queryKey: ['user-licenses'],
    queryFn: fetchUserLicenses,
  });

  const updateProfileMutation = useMutation({
    mutationFn: userApi.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      enqueueSnackbar('プロフィールを更新しました', { variant: 'success' });
      setEditDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('プロフィールの更新に失敗しました', { variant: 'error' });
    }
  });

  const handleEditProfile = () => {
    setEditDialogOpen(true);
  };

  const handleSaveProfile = (data: UpdateProfileRequest) => {
    updateProfileMutation.mutate(data);
  };

  const isLoading = profileLoading || statsLoading;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box>
        <Alert severity="error">
          ユーザー情報を取得できませんでした。
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        マイページ
      </Typography>

      <Grid container spacing={3}>
        {/* ユーザープロフィールカード */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">プロフィール</Typography>
                <IconButton onClick={handleEditProfile} size="small" color="primary">
                  <EditIcon />
                </IconButton>
              </Box>

              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}>
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6">{profile.display_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.role}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      メールアドレス
                    </Typography>
                    <Typography variant="body2">{profile.email}</Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <BadgeIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ユーザーID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {profile.id.substring(0, 8)}...
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <DateRangeIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      登録日
                    </Typography>
                    <Typography variant="body2">
                      {profile.created_at && profile.created_at !== '2025-01-01T00:00:00Z'
                        ? format(new Date(profile.created_at), 'yyyy年MM月dd日', { locale: ja })
                        : '-'}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* 統計情報 */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {/* ライセンス統計 */}
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LicenseIcon color="primary" />
                    <Typography variant="h6">ライセンス</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {stats?.licenses.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    アクティブ: {stats?.licenses.active || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* デバイス統計 */}
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <DeviceHubIcon color="success" />
                    <Typography variant="h6">デバイス</Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {stats?.devices.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    アクティブ: {stats?.devices.active || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* アプリケーション統計 */}
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AppsIcon color="info" />
                    <Typography variant="h6">アプリ</Typography>
                  </Box>
                  <Typography variant="h4" color="info.main">
                    {stats?.applications.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    管理中
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* ライセンス情報 */}
        {licenses && licenses.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ライセンス情報
                </Typography>
                <Grid container spacing={2}>
                  {licenses.map((license) => (
                    <Grid item xs={12} key={license.id}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <Typography variant="subtitle2" color="text.secondary">
                              ライセンスID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {license.id}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="text.secondary">
                              状態
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                {
                                  active: 'アクティブ',
                                  inactive: '非アクティブ',
                                  expired: '期限切れ',
                                }[license.status]
                              }
                              color={
                                {
                                  active: 'success',
                                  inactive: 'default',
                                  expired: 'error',
                                }[license.status] as any
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="subtitle2" color="text.secondary">
                              アクティベーション数
                            </Typography>
                            <Typography variant="body2">
                              {license.activationCount} / {license.maxActivations}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="text.secondary">
                              有効期限
                            </Typography>
                            <Typography variant="body2">
                              {new Date(license.expiryDate).toLocaleDateString('ja-JP')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <ProfileEditDialog
        profile={profile}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveProfile}
      />
    </Box>
  );
} 